import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, string>} */
  #cachedDescription = new Map();
  /** @type {AbortController | null} */
  #familySwatchAbort = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    await this.#showProduct(this.productPageUrl);

    this.#openQuickAddModal();
    this.#listenForFamilySwatches();
  };

  /**
   * Fetches (or reuses) a product page's grid and renders it into the modal.
   * Shared by the initial open and the family colour chooser, which loads the
   * sibling product's page into the open modal instead of navigating away.
   * @param {string} productPageUrl - The product page to show
   * @returns {Promise<boolean>} Whether the modal content was updated
   */
  async #showProduct(productPageUrl) {
    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(productPageUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(productPageUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(productPageUrl, productGrid);
        }
        const metaDescription = html.querySelector('meta[name="description"]');
        if (metaDescription instanceof HTMLMetaElement && metaDescription.content) {
          this.#cachedDescription.set(productPageUrl, metaDescription.content);
        }
      }
    }

    if (!productGrid) return false;

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent, productPageUrl);
    return true;
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @param {string} [productPageUrl] - The page the grid came from (defaults to this card's product)
   */
  async updateQuickAddModal(productGrid, productPageUrl = this.productPageUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      // The sg-config buy box now carries its own <product-price> (so the pack-size
      // price updates via product-price.js). That makes the four-piece check below
      // pass, but the stock compact rebuild empties the sheet for this store's
      // markup — so skip it whenever the custom buy box is present and fall through
      // to the full-grid morph, which is what this store already relied on.
      const usesSgConfig = productGrid.querySelector('.sg-config');

      // The stock compact rebuild needs all four stock pieces. This store's PDP
      // replaces the stock price block with the sg-config buy box, so when any
      // piece is missing (or the sg-config buy box is present) we fall through and
      // morph the full grid instead — otherwise the sheet opens empty.
      if (!usesSgConfig && productDetails && productFormComponent && variantPicker && productPrice) {
        const productTitle = document.createElement('a');
        productTitle.textContent = this.dataset.productTitle || '';

        // Make product title as a link to the product page
        productTitle.href = productPageUrl;

        const productHeader = document.createElement('div');
        productHeader.classList.add('product-header');

        productHeader.appendChild(productTitle);
        productHeader.appendChild(productPrice);
        productGrid.appendChild(productHeader);
        productGrid.appendChild(variantPicker);
        productGrid.appendChild(productFormComponent);
        productDetails.remove();
      }
    }

    // A different product than the modal currently holds: start from an empty modal
    // so every component (form, picker, quantity, media) is created fresh instead of
    // being reconciled onto elements that already ran their lifecycle for the
    // previous product. Reconciling reused the previous product's form component
    // with its listeners torn down, so the hidden variant id stopped following pack
    // changes (the cart would receive the wrong pack). The same product (re-open,
    // pack change) still morphs in place.
    const currentProductId = modalContent
      .querySelector('product-form-component, variant-picker')
      ?.getAttribute('data-product-id');
    const nextProductId = productGrid
      .querySelector('product-form-component, variant-picker')
      ?.getAttribute('data-product-id');
    if (currentProductId && nextProductId && currentProductId !== nextProductId) {
      modalContent.replaceChildren();
    }

    morph(modalContent, productGrid);

    this.#injectQuickShopExtras(modalContent, productPageUrl);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Adds the model-store Quick Shop pieces the morphed PDP grid doesn't carry:
   * a collection label above the title, a short description under the rating,
   * and a View Details link after the buy buttons. Desktop-only via CSS.
   * @param {Element} modalContent - The modal content element
   * @param {string} productPageUrl - The page the modal content came from
   */
  #injectQuickShopExtras(modalContent, productPageUrl) {
    const productUrl = productPageUrl.split('?')[0];

    const titleEl = modalContent.querySelector('.view-product-title');
    if (titleEl?.parentElement && !modalContent.querySelector('.qs-collection-link')) {
      const collectionLink = document.createElement('a');
      collectionLink.className = 'qs-collection-link';
      collectionLink.href = '/collections/all-products';
      collectionLink.textContent = 'All Products';
      titleEl.parentElement.insertBefore(collectionLink, titleEl);
    }

    // The PDP renders the rating block above the title; the model popup puts
    // the stars under the title, so move them there.
    const rating = modalContent.querySelector('.sg-rating');
    if (titleEl && rating) {
      titleEl.after(rating);
    }

    const description = this.#cachedDescription.get(productPageUrl);
    if (description && !modalContent.querySelector('.qs-description')) {
      const descriptionEl = document.createElement('p');
      descriptionEl.className = 'qs-description';
      descriptionEl.textContent = description;
      if (rating && titleEl) {
        rating.after(descriptionEl);
      } else if (titleEl?.parentElement) {
        titleEl.after(descriptionEl);
      }
    }

    const buyButtons = modalContent.querySelector('.buy-buttons-block');
    if (buyButtons?.parentElement && productUrl && !modalContent.querySelector('.qs-view-details')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'qs-view-details';
      const link = document.createElement('a');
      link.className = 'qs-view-details__button';
      link.href = productUrl;
      link.append('View Details ');
      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '↗';
      link.appendChild(arrow);
      wrapper.appendChild(link);
      buyButtons.after(wrapper);
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * The family colour chooser (blocks/product-family-swatches.liquid) links the
   * per-colour products of one family. On the product page a click navigates;
   * inside Quick Shop it should behave like the colour option used to, so the
   * click is intercepted and the sibling's page is loaded into the open modal.
   * One delegated listener per open; it is removed when the dialog closes.
   */
  #listenForFamilySwatches() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(dialogComponent instanceof QuickAddDialog) || !modalContent) return;

    this.#familySwatchAbort?.abort();
    const controller = new AbortController();
    this.#familySwatchAbort = controller;

    modalContent.addEventListener('click', this.#handleFamilySwatchClick, { signal: controller.signal });
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => controller.abort(), {
      once: true,
      signal: controller.signal,
    });
  }

  /** @param {Event} event */
  #handleFamilySwatchClick = async (event) => {
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest('a.family-swatches__swatch[href]');
    if (!(swatch instanceof HTMLAnchorElement)) return;

    event.preventDefault();

    const modalContent = document.getElementById('quick-add-modal-content');
    // Keep the shopper's pack size (and any other choice) across the colour change.
    const keep = modalContent ? this.#checkedOptionValues(modalContent) : [];

    const shown = await this.#showProduct(new URL(swatch.href, window.location.href).toString());

    if (shown && modalContent) this.#restoreOptionValues(modalContent, keep);
  };

  /**
   * @param {Element} modalContent - The modal content element
   * @returns {string[]} The values of the checked option radios in the modal
   */
  #checkedOptionValues(modalContent) {
    /** @type {string[]} */
    const values = [];
    for (const input of modalContent.querySelectorAll('variant-picker input[type="radio"]:checked')) {
      if (input instanceof HTMLInputElement) values.push(input.value);
    }
    return values;
  }

  /**
   * Re-selects option values on the freshly rendered sibling (same values, new
   * product) and lets the variant picker fetch the matching variant.
   * @param {Element} modalContent - The modal content element
   * @param {string[]} values - Option values to re-select
   */
  #restoreOptionValues(modalContent, values) {
    /** @type {HTMLInputElement | null} */
    let changed = null;
    for (const value of values) {
      for (const input of modalContent.querySelectorAll('variant-picker input[type="radio"]')) {
        if (!(input instanceof HTMLInputElement) || input.value !== value || input.disabled) continue;
        if (!input.checked) {
          input.checked = true;
          changed = input;
        }
        break;
      }
    }
    changed?.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
